import { STOCK_BALLS } from './stock-manifest';

/**
 * Ball artwork. The game only ever asks for "an image for rank N"; where it came from
 * — a user's cropped photo or a generated placeholder — is not its problem.
 */

export interface BallArt {
  /** Square image drawn clipped to the ball's circle, or null for a plain fill. */
  imageFor(rank: number): CanvasImageSource | null;
}

/**
 * Stand-in art until the create flow exists: fruit on a soft tint, echoing Suika and
 * making it obvious at a glance that these aren't real photos yet.
 */
const PLACEHOLDER_GLYPHS = [
  '🍒', '🍓', '🍇', '🍊', '🍋', '🍎', '🍐', '🥝',
  '🍑', '🍍', '🥥', '🍈', '🍉', '🎃', '🌻',
];

const SPRITE_SIZE = 256;

function renderGlyph(glyph: string): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = SPRITE_SIZE;
  canvas.height = SPRITE_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;

  // Soft radial tint so the ball doesn't read as a sticker on flat white.
  const bg = ctx.createRadialGradient(
    SPRITE_SIZE * 0.38,
    SPRITE_SIZE * 0.32,
    SPRITE_SIZE * 0.1,
    SPRITE_SIZE * 0.5,
    SPRITE_SIZE * 0.5,
    SPRITE_SIZE * 0.62,
  );
  bg.addColorStop(0, '#ffffff');
  bg.addColorStop(1, '#f6ece4');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, SPRITE_SIZE, SPRITE_SIZE);

  ctx.font = `${SPRITE_SIZE * 0.6}px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(glyph, SPRITE_SIZE / 2, SPRITE_SIZE * 0.54);
  return canvas;
}

/** Lazily renders each glyph once, then reuses the sprite for every ball of that rank. */
export function createPlaceholderArt(ballCount: number): BallArt {
  const cache = new Map<number, HTMLCanvasElement>();
  return {
    imageFor(rank) {
      if (rank < 1 || rank > ballCount) return null;
      let sprite = cache.get(rank);
      if (!sprite) {
        // Spread the glyphs across however many ranks this game has, so a 5-ball game
        // still gets visibly different fruit rather than the first five.
        const index =
          ballCount <= 1
            ? 0
            : Math.round(((rank - 1) / (ballCount - 1)) * (PLACEHOLDER_GLYPHS.length - 1));
        sprite = renderGlyph(PLACEHOLDER_GLYPHS[index] ?? '🍒');
        cache.set(rank, sprite);
      }
      return sprite;
    },
  };
}

/** Art backed by already-loaded photo images, keyed by rank. Used from phase 3 on. */
export function createPhotoArt(images: Map<number, CanvasImageSource>): BallArt {
  return { imageFor: (rank) => images.get(rank) ?? null };
}

/**
 * Loads a saved game's ball images.
 *
 * Waits for every one before returning, because a ball that pops in halfway through a
 * game looks like a glitch. `decode()` also guarantees the bitmap is ready, so the
 * first frame that draws it doesn't stall.
 */
export async function loadBallArt(keys: string[]): Promise<BallArt> {
  const images = new Map<number, CanvasImageSource>();
  await Promise.all(
    keys.map(async (key, index) => {
      const img = new Image();
      img.src = `/${key}`;
      await img.decode();
      images.set(index + 1, img);
    }),
  );
  return createPhotoArt(images);
}

/** Picks `count` distinct stock images at random. */
export function pickStockBalls(count: number): string[] {
  const pool = [...STOCK_BALLS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  // If a game somehow wants more balls than there are photos, repeat rather than fail.
  return Array.from({ length: count }, (_, i) => pool[i % pool.length]!);
}

export function stockUrl(name: string): string {
  return `/stock/${name}`;
}

/** A random set of stock faces, for Quick Play. */
export async function loadStockArt(count: number): Promise<BallArt> {
  return loadBallArt(pickStockBalls(count).map((name) => `stock/${name}`));
}
